import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";
import { z } from "zod";
import { apiHooks, FeedbackTopicEnum } from "@/api";
import { LocalStorageUtils } from "@/shared/lib/storage";
import { UIKit } from "@/shared/ui";
import { useFeedbackStore } from "../logic";

const AUTO_PROMPT_DELAY = 600_000;

const FEEDBACK_TOPIC_LABELS: Record<FeedbackTopicEnum, string> = {
	[FeedbackTopicEnum.Plan2D]: "Создание 2D-плана",
	[FeedbackTopicEnum.View3D]: "3D-вид",
	[FeedbackTopicEnum.Elevation]: "Развертка",
	[FeedbackTopicEnum.Performance]: "Скорость работы",
	[FeedbackTopicEnum.CatalogExpansion]: "Расширить каталог объектов",
	[FeedbackTopicEnum.MoreMaterials]: "Больше материалов",
	[FeedbackTopicEnum.Interface]: "Интерфейс",
	[FeedbackTopicEnum.Other]: "Другое",
};

const FEEDBACK_TOPICS = Object.values(FeedbackTopicEnum).filter(
	(topic): topic is FeedbackTopicEnum => typeof topic === "number",
);

const feedbackSchema = z.object({
	text: z.string().trim().min(1, "Опишите, что можно улучшить"),
	topics: z.array(z.nativeEnum(FeedbackTopicEnum)),
});

/** Отзыв пользователя: текст остаётся в памяти, localStorage хранит только технический статус. */
export const FeedbackWidget = () => {
	const isOpen = useFeedbackStore((state) => state.isOpen);
	const open = useFeedbackStore((state) => state.open);
	const close = useFeedbackStore((state) => state.close);
	const sendFeedback = apiHooks.feedback.useSendFeedback();
	const [submitError, setSubmitError] = useState(false);
	const form = useForm({
		defaultValues: { text: "", topics: [] as FeedbackTopicEnum[] },
		validators: { onChange: feedbackSchema },
		onSubmit: async ({ value }) => {
			setSubmitError(false);
			try {
				const response = await sendFeedback.execute(value);
				if (response.error !== null) {
					setSubmitError(true);
					return;
				}
				close();
			} catch {
				setSubmitError(true);
			}
		},
	});
	useEffect(() => {
		const startedAt = Date.now();
		const prompt = LocalStorageUtils.getFeedbackPrompt();
		const showFeedback = () => {
			LocalStorageUtils.setFeedbackPrompt({ ...prompt, wasShown: true });
			open();
		};
		const remaining = Math.max(0, AUTO_PROMPT_DELAY - prompt.timeSpentMs);
		const timer = prompt.wasShown ? undefined : window.setTimeout(showFeedback, remaining);
		return () => {
			if (timer !== undefined) window.clearTimeout(timer);
			const currentPrompt = LocalStorageUtils.getFeedbackPrompt();
			LocalStorageUtils.setFeedbackPrompt({
				wasShown: currentPrompt.wasShown,
				timeSpentMs: prompt.timeSpentMs + Date.now() - startedAt,
			});
		};
	}, [open]);
	if (!isOpen) return null;
	return (
		<UIKit.Modal ariaLabel="Отзыв" onClose={close} className="p-5 md:max-w-lg">
			<UIKit.Text size="xl">Что можно сделать лучше?</UIKit.Text>
			<UIKit.Text className="mt-2 text-sm text-ink">
				Ваш отзыв поможет улучшить планировщик.
			</UIKit.Text>
			<UIKit.Text className="mt-4 text-sm font-medium">
				Выберите темы, которые вас волнуют
			</UIKit.Text>
			<form action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="topics">
					{(field) => (
						<div className="mt-2 flex flex-wrap gap-2">
							{FEEDBACK_TOPICS.map((topic) => (
								<UIKit.Checkbox
									key={topic}
									className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs"
									label={FEEDBACK_TOPIC_LABELS[topic]}
									checked={field.state.value.includes(topic)}
									onChange={() =>
										field.handleChange((current: FeedbackTopicEnum[]) =>
											current.includes(topic)
												? current.filter((item) => item !== topic)
												: [...current, topic],
										)
									}
								/>
							))}
						</div>
					)}
				</UIKit.FormField>
				<UIKit.FormField form={form} name="text">
					{(field) => (
						<div className="mt-4">
							<UIKit.Textarea
								label="Опишите, что произошло и как это можно улучшить"
								value={field.state.value}
								onChange={field.handleChange}
							/>
						</div>
					)}
				</UIKit.FormField>
				{submitError && (
					<UIKit.Text className="mt-2 text-sm text-red-600">
						Не удалось отправить отзыв. Попробуйте ещё раз.
					</UIKit.Text>
				)}
				<form.Subscribe selector={(state) => state.isSubmitting}>
					{(isSubmitting) => (
						<div className="mt-4 flex gap-2">
							<UIKit.Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "Отправляем" : "Отправить"}
							</UIKit.Button>
						</div>
					)}
				</form.Subscribe>
			</form>
		</UIKit.Modal>
	);
};
